import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { type FC, type MouseEvent, type ReactNode, useCallback, useRef } from "react";
import { ALPHA, FONT_SIZE, INK, LETTER_SPACING, MOTION, NEUTRAL, SURFACE, VALUE_CHOICE } from "../../../theme/tokens";

export type ValueChoiceItem = {
	/** Identity handed back to `onSelect`. */
	value: number | string;
	/** The figure, large and leading — what the user is actually choosing. */
	label: string;
	/** Small caption under the figure saying what the figure MEANS ("30th", "median"). Already translated. */
	caption?: string;
	/**
	 * Small uppercase label ABOVE the figure — the tile's kind ("8 hours", "Custom"). Already translated;
	 * the grid applies the uppercase. Pair it with `caption` only when both genuinely say something.
	 */
	eyebrow?: string;
	/** Leading glyph beside the figure (a sun/moon, a clock). Sized by the figure's font — pass `fontSize="inherit"` icons. */
	icon?: ReactNode;
	/**
	 * Dashed resting border: an open-ended tile ("Custom · Set hours") that asks for a value rather than
	 * being one. Once selected it takes the same selected treatment as every other tile.
	 */
	dashed?: boolean;
	/** Visible but not pickable (native `disabled`: out of the tab order, clicks ignored). */
	disabled?: boolean;
	/** Accessible name; the figure alone rarely says what picking it does. Defaults to eyebrow + figure. */
	ariaLabel?: string;
	dataTestId?: string;
	/**
	 * A secondary action on this tile — a "Clear" on a Custom card that holds a value. Rendered as its own
	 * button beside (never inside) the tile's radio, pinned in the top-right slot the outline check uses; the
	 * check stands down while it shows, since the ring + wash already say "picked". Built for the outline look.
	 */
	action?: ValueChoiceTileAction;
};

/** A tile's secondary action. Focus returns to the tile after it runs, since the action usually unmounts itself. */
export type ValueChoiceTileAction = {
	/** Visible, already-translated label ("Clear"). */
	label: string;
	/** Accessible name saying what it does to THIS tile ("Clear custom hours"). */
	ariaLabel: string;
	onClick: () => void;
	/** Leading glyph; pass `fontSize="inherit"` icons. */
	icon?: ReactNode;
	dataTestId?: string;
};

/**
 * `"solid"` — compact centred tile, selected = filled with the accent (the original look).
 * `"outline"` — a left-aligned card: white, hairline border, selected = brand ring + soft wash + a
 * check-circle in the top-right corner. For wide cards carrying an eyebrow/icon (shift windows).
 */
export type ValueChoiceTileVariant = "solid" | "outline";

type Props = {
	items: ValueChoiceItem[];
	/** The currently chosen value, or `null` when the user is off every preset (a custom value). */
	selected: number | string | null;
	/**
	 * Fires on every pick, including a re-click of the selected tile. `event.currentTarget` is the tile
	 * itself, so a tile that opens something (a "Custom" tile → a time popover) can anchor to it.
	 */
	onSelect: (value: number | string, event: MouseEvent<HTMLElement>) => void;
	/** Tiles per row. Responsive object accepted, since a preset grid wants fewer columns on a phone. */
	columns?: number | Partial<Record<"xs" | "sm" | "md", number>>;
	/**
	 * Fill of the selected tile. Defaults to the org's brand primary; pass a fixed accent (e.g.
	 * `ACCENT_BLUE.MAIN`) on a surface whose design specifies one, so the selected tile matches the
	 * other accent-coloured controls around it.
	 */
	accentColor?: string;
	/** Tile look. Defaults to `"solid"`. */
	tileVariant?: ValueChoiceTileVariant;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

type TileState = { isSelected: boolean; accent: string; accentHover: string; item: ValueChoiceItem };

const toTemplate = (columns: Props["columns"]) => {
	if (typeof columns === "number") {
		return `repeat(${columns}, minmax(0, 1fr))`;
	}
	const entries = Object.entries(columns ?? { xs: 2, sm: 4 });
	return Object.fromEntries(entries.map(([breakpoint, count]) => [breakpoint, `repeat(${count}, minmax(0, 1fr))`]));
};

/** Resting border of an unselected tile — dashed tiles use the darker neutral so the dashes stay visible. */
const restingBorder = (item: ValueChoiceItem) => `1px ${item.dashed ? "dashed" : "solid"} ${item.dashed ? NEUTRAL[450] : NEUTRAL[200]}`;

/** Class on the glyph wrapper, so the disabled state can mute a caller-coloured icon (an amber sun). */
const ICON_CLASS = "ValueChoiceGrid-icon";

const disabledSx = {
	cursor: "not-allowed",
	borderColor: NEUTRAL[200],
	backgroundColor: NEUTRAL[50],
	color: INK.DISABLED,
	boxShadow: "none",
	[`& .${ICON_CLASS}`]: { opacity: ALPHA.STRUCK },
} as const;

const solidTileSx = (theme: Theme, { isSelected, accent, accentHover, item }: TileState) => ({
	alignItems: "center",
	justifyContent: "center",
	gap: 0.125,
	minHeight: VALUE_CHOICE.TILE_MIN_HEIGHT,
	px: 1,
	py: 0.75,
	border: isSelected ? `1px solid ${accent}` : restingBorder(item),
	backgroundColor: isSelected ? accent : SURFACE,
	color: isSelected ? SURFACE : INK.PRIMARY,
	transition: theme.transitions.create(["background-color", "border-color", "color", "transform"], {
		duration: MOTION.DURATION.CONTROL,
		easing: MOTION.EASE_OUT,
	}),
	"@media (hover: hover) and (pointer: fine)": {
		"&:hover:not(:disabled)": {
			borderColor: accent,
			backgroundColor: isSelected ? accentHover : alpha(accent, ALPHA.SELECTED_OVERLAY),
		},
	},
});

const outlineTileSx = (theme: Theme, { isSelected, accent, item }: TileState) => ({
	alignItems: "stretch",
	textAlign: "left",
	gap: 1,
	px: 2,
	py: 1.75,
	border: isSelected ? `1px solid ${accent}` : restingBorder(item),
	backgroundColor: isSelected ? alpha(accent, ALPHA.SELECTED_OVERLAY) : SURFACE,
	// The inset ring thickens the edge to 2px without moving the content; the lift sits under it.
	boxShadow: isSelected ? `inset 0 0 0 ${VALUE_CHOICE.OUTLINE.SELECTED_RING}px ${accent}, ${VALUE_CHOICE.OUTLINE.selectedShadow(accent)}` : "none",
	color: INK.PRIMARY,
	transition: theme.transitions.create(["background-color", "border-color", "box-shadow", "transform"], {
		duration: MOTION.DURATION.COLOR_SHIFT,
		easing: MOTION.EASE_OUT,
	}),
	"@media (hover: hover) and (pointer: fine)": {
		"&:hover:not(:disabled)": {
			borderColor: accent,
			...(!isSelected && { backgroundColor: alpha(accent, ALPHA.HOVER_OVERLAY) }),
		},
	},
});

/** The brand check that lands in the selected outline card. Always mounted so selecting never reflows the row. */
const OutlineCheck: FC<{ visible: boolean; accent: string }> = ({ visible, accent }) => (
	<Box
		component="span"
		aria-hidden
		sx={(theme) => ({
			display: "inline-flex",
			flexShrink: 0,
			color: accent,
			fontSize: VALUE_CHOICE.OUTLINE.CHECK_SIZE,
			opacity: visible ? 1 : 0,
			transform: visible ? "scale(1)" : `scale(${VALUE_CHOICE.OUTLINE.CHECK_ENTER_SCALE})`,
			transition: theme.transitions.create(["opacity", "transform"], { duration: MOTION.DURATION.ENTRANCE_SM, easing: MOTION.EASE_OUT }),
			"@media (prefers-reduced-motion: reduce)": { transform: "none" },
		})}
	>
		<CheckCircleRoundedIcon fontSize="inherit" />
	</Box>
);

const Eyebrow: FC<{ text: string; muted: boolean }> = ({ text, muted }) => (
	<Typography
		component="span"
		noWrap
		sx={{
			fontSize: FONT_SIZE.LABEL_XS,
			fontWeight: 600,
			letterSpacing: LETTER_SPACING.LABEL,
			textTransform: "uppercase",
			lineHeight: VALUE_CHOICE.COMPACT_LINE_HEIGHT,
			color: muted ? "inherit" : INK.MUTED,
			opacity: muted ? VALUE_CHOICE.SELECTED_SECONDARY_OPACITY : 1,
		}}
	>
		{text}
	</Typography>
);

/** The figure, with its optional leading glyph. Without a glyph it is exactly the original single span. */
const Figure: FC<{ item: ValueChoiceItem; outline: boolean; ink: string | undefined }> = ({ item, outline, ink }) => {
	const figureSx = outline
		? {
				fontSize: FONT_SIZE.TITLE_SM,
				fontWeight: item.dashed ? 500 : 600,
				lineHeight: VALUE_CHOICE.COMPACT_LINE_HEIGHT,
				fontVariantNumeric: "tabular-nums",
				color: ink,
			}
		: { fontSize: FONT_SIZE.TITLE_MD, fontWeight: 800, lineHeight: 1.15, fontVariantNumeric: "tabular-nums" };
	const text = (
		<Typography component="span" sx={figureSx}>
			{item.label}
		</Typography>
	);
	if (!item.icon) return text;
	// The glyph box is one figure line tall and top-aligned, so on a narrow tile where the figure wraps
	// the glyph stays beside its FIRST line instead of floating to the middle of the block.
	return (
		<Box component="span" sx={{ display: "inline-flex", alignItems: "flex-start", gap: 0.75, minWidth: 0 }}>
			<Box
				component="span"
				aria-hidden
				className={ICON_CLASS}
				sx={{ display: "inline-flex", alignItems: "center", flexShrink: 0, height: `${figureSx.lineHeight}em`, fontSize: figureSx.fontSize }}
			>
				{item.icon}
			</Box>
			{text}
		</Box>
	);
};

const Caption: FC<{ text: string; isSelected: boolean; outline: boolean }> = ({ text, isSelected, outline }) => (
	<Typography
		component="span"
		sx={
			outline
				? { fontSize: FONT_SIZE.CAPTION, lineHeight: VALUE_CHOICE.COMPACT_LINE_HEIGHT, color: INK.MUTED }
				: {
						fontSize: FONT_SIZE.LABEL_MICRO,
						fontWeight: 700,
						letterSpacing: LETTER_SPACING.LABEL,
						textTransform: "uppercase",
						lineHeight: 1.2,
						color: isSelected ? "inherit" : NEUTRAL[500],
						opacity: isSelected ? VALUE_CHOICE.SELECTED_SECONDARY_OPACITY : 1,
					}
		}
	>
		{text}
	</Typography>
);

/** Outline card body: [eyebrow | figure] + check on the first line, then the figure (under an eyebrow) and caption. */
const OutlineBody: FC<{ item: ValueChoiceItem; isSelected: boolean; accent: string; accentHover: string }> = ({ item, isSelected, accent, accentHover }) => {
	// A dashed ("set a value") tile speaks in the brand's dark ink until it holds a value.
	const figureInk = item.dashed && !isSelected && !item.disabled ? accentHover : undefined;
	const figure = <Figure item={item} outline ink={figureInk} />;
	return (
		<>
			<Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minWidth: 0 }}>
				{item.eyebrow ? <Eyebrow text={item.eyebrow} muted={false} /> : figure}
				<OutlineCheck visible={isSelected && !item.action} accent={accent} />
			</Box>
			{item.eyebrow && figure}
			{item.caption && <Caption text={item.caption} isSelected={isSelected} outline />}
		</>
	);
};

const SolidBody: FC<{ item: ValueChoiceItem; isSelected: boolean }> = ({ item, isSelected }) => (
	<>
		{item.eyebrow && <Eyebrow text={item.eyebrow} muted={isSelected} />}
		<Figure item={item} outline={false} ink={undefined} />
		{item.caption && <Caption text={item.caption} isSelected={isSelected} outline={false} />}
	</>
);

const defaultAriaLabel = (item: ValueChoiceItem) => (item.eyebrow ? `${item.eyebrow}, ${item.label}` : item.label);

type TileProps = {
	item: ValueChoiceItem;
	isSelected: boolean;
	outline: boolean;
	accent: string;
	accentHover: string;
	onSelect: Props["onSelect"];
	/** The tile sits in an action wrapper and fills it. */
	fill: boolean;
};

/** One tile: a real `<button role="radio">`. */
const Tile: FC<TileProps> = ({ item, isSelected, outline, accent, accentHover, onSelect, fill }) => (
	<Box
		component="button"
		type="button"
		role="radio"
		aria-checked={isSelected}
		aria-label={item.ariaLabel ?? defaultAriaLabel(item)}
		disabled={item.disabled}
		data-testid={item.dataTestId}
		onClick={(event: MouseEvent<HTMLElement>) => onSelect(item.value, event)}
		sx={(tileTheme) => {
			const state: TileState = { isSelected, accent, accentHover, item };
			return {
				display: "flex",
				flexDirection: "column",
				font: "inherit",
				cursor: "pointer",
				borderRadius: 1.5,
				...(fill && { flex: 1, minWidth: 0 }),
				...(outline ? outlineTileSx(tileTheme, state) : solidTileSx(tileTheme, state)),
				"&:active:not(:disabled)": { transform: "scale(0.97)" },
				"&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: 2 },
				"&:disabled": disabledSx,
				"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active:not(:disabled)": { transform: "none" } },
			};
		}}
	>
		{outline ? (
			<OutlineBody item={item} isSelected={isSelected} accent={accent} accentHover={accentHover} />
		) : (
			<SolidBody item={item} isSelected={isSelected} />
		)}
	</Box>
);

/** The tile's secondary action: a quiet brand link in the card's top-right slot, landing softly as it appears. */
const TileActionButton: FC<{ action: ValueChoiceTileAction; accentHover: string; onDone: () => void }> = ({ action, accentHover, onDone }) => (
	<Box
		component="button"
		type="button"
		aria-label={action.ariaLabel}
		data-testid={action.dataTestId}
		onClick={() => {
			action.onClick();
			onDone();
		}}
		sx={(theme) => ({
			"@keyframes valueChoiceActionEnter": {
				from: { opacity: 0, transform: `scale(${VALUE_CHOICE.OUTLINE.ACTION_ENTER_SCALE})` },
				to: { opacity: 1, transform: "scale(1)" },
			},
			position: "absolute",
			top: VALUE_CHOICE.OUTLINE.ACTION_INSET,
			insetInlineEnd: VALUE_CHOICE.OUTLINE.ACTION_INSET,
			display: "inline-flex",
			alignItems: "center",
			gap: 0.5,
			height: VALUE_CHOICE.OUTLINE.ACTION_HEIGHT,
			px: `${VALUE_CHOICE.OUTLINE.ACTION_PADDING_X}px`,
			border: "none",
			borderRadius: 1,
			backgroundColor: "transparent",
			color: accentHover,
			font: "inherit",
			fontSize: FONT_SIZE.CAPTION,
			fontWeight: 600,
			cursor: "pointer",
			animation: `valueChoiceActionEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
			transition: theme.transitions.create(["background-color", "transform"], { duration: MOTION.DURATION.CONTROL, easing: MOTION.EASE_OUT }),
			"@media (hover: hover) and (pointer: fine)": { "&:hover": { backgroundColor: alpha(accentHover, ALPHA.SELECTED_OVERLAY) } },
			"&:active": { transform: "scale(0.97)" },
			"&:focus-visible": { outline: `2px solid ${accentHover}`, outlineOffset: 1 },
			"@media (prefers-reduced-motion: reduce)": { animation: "none", transition: "none", "&:active": { transform: "none" } },
		})}
	>
		{action.icon && (
			<Box component="span" aria-hidden sx={{ display: "inline-flex", fontSize: FONT_SIZE.EMPHASIS }}>
				{action.icon}
			</Box>
		)}
		{action.label}
	</Box>
);

/**
 * A tile that carries a secondary action. The action is a sibling of the radio (a button may not nest in a
 * button); after it runs, `onDone` hands focus back to the tile so a keyboard user isn't dropped onto the page.
 */
const ActionTile: FC<Omit<TileProps, "fill"> & { action: ValueChoiceTileAction; onDone: () => void }> = ({ action, onDone, ...tile }) => (
	<Box sx={{ position: "relative", display: "flex", minWidth: 0 }}>
		<Tile {...tile} fill />
		<TileActionButton action={action} accentHover={tile.accentHover} onDone={onDone} />
	</Box>
);

/**
 * Focus the tile at `index` once React has committed. An action usually removes itself, which re-renders
 * its tile as a plain one (a new button), so the tile is looked up by position after that render lands.
 */
const useRefocusTile = () => {
	const gridRef = useRef<HTMLDivElement>(null);
	const refocus = useCallback((index: number) => {
		requestAnimationFrame(() => gridRef.current?.querySelectorAll<HTMLElement>('[role="radio"]')[index]?.focus());
	}, []);
	return { gridRef, refocus };
};

/**
 * A grid of selectable **value tiles**: each tile leads with the figure and captions what it means
 * underneath, and picking one IS the answer (no confirm) — the preset picker for "choose one of these
 * numbers" where plain chips lose the caption and a dropdown hides the comparison.
 *
 * Generic over what the numbers are (census percentiles, hour presets, shift windows, plan tiers): the
 * caller pre-formats every string, so the grid owns only the tile presentation, the selected state and
 * the keyboard/`aria` semantics (a real `<button role="radio">` per tile inside a `radiogroup`).
 * `tileVariant="outline"` swaps the filled tile for a card with an eyebrow, a leading glyph and a check.
 *
 * For value-only chips reach for `SystemChips`; for a two-or-three-way either/or use `SystemFlatSwitch`.
 */
export const ValueChoiceGrid: FC<Props> = ({ items, selected, onSelect, columns, accentColor, tileVariant = "solid", dataTestId, sx }) => {
	const theme = useTheme();
	const outline = tileVariant === "outline";
	const accent = accentColor ?? theme.palette.primary.main;
	const accentHover = accentColor ?? theme.palette.primary.dark;
	const { gridRef, refocus } = useRefocusTile();
	return (
		<Box
			ref={gridRef}
			role="radiogroup"
			data-testid={dataTestId}
			sx={[{ display: "grid", gridTemplateColumns: toTemplate(columns), gap: outline ? 1.5 : 1 }, ...(Array.isArray(sx) ? sx : [sx])]}
		>
			{items.map((item, index) => {
				const tile = { item, isSelected: selected === item.value, outline, accent, accentHover, onSelect };
				if (!item.action) return <Tile key={item.value} {...tile} fill={false} />;
				return <ActionTile key={item.value} {...tile} action={item.action} onDone={() => refocus(index)} />;
			})}
		</Box>
	);
};

export default ValueChoiceGrid;
